import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Hourglass, ArrowRight, Lock } from "@phosphor-icons/react";
import { useAdminAuth } from "../context/AdminAuthContext";
import adminApi from "../lib/adminApi";

export default function AdminLogin() {
  const { login } = useAdminAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@vitae-publica.tech");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const adm = await login(email, password);
      toast.success("Console admin ouverte");
      if (adm.must_change_password) {
        toast.warning("Veuillez changer le mot de passe initial");
        nav("/admin/settings");
      } else {
        nav("/admin");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Identifiants invalides");
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await adminApi.post("/password/forgot", { email });
      toast.success(`Lien de réinitialisation envoyé à ${r.data.to}`);
      setMode("login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] grid grid-cols-1 lg:grid-cols-2" data-testid="admin-login-page">
      {/* Panneau gauche — identité admin */}
      <div className="hidden lg:flex flex-col justify-between bg-[var(--ink)] text-[var(--paper)] p-12 border-r border-black">
        <div>
          <div className="flex items-center gap-3">
            <Lock weight="fill" size={28} className="text-[var(--signal)]" />
            <div>
              <div className="font-serif text-2xl font-semibold">VITA-LEX</div>
              <div className="label-mono opacity-70">CONSOLE ADMINISTRATEUR</div>
            </div>
          </div>
          <h2 className="mt-24 font-serif text-5xl leading-tight font-medium">
            Console<br />de <span className="italic text-[var(--signal)]">souveraineté</span><br />éditoriale.
          </h2>
          <p className="mt-8 text-[var(--paper)]/70 max-w-md leading-relaxed">
            Pilotage des modèles IA, gestion du contenu public, mesure d'audience et configuration de la plateforme.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[["LLM", "Modèles IA"], ["CMS", "Contenus"], ["§", "Analytics"]].map(([k, v]) => (
            <div key={k} className="bg-[var(--ink)] p-5">
              <div className="font-serif text-2xl">{k}</div>
              <div className="label-mono opacity-70 mt-1">{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Panneau droit — formulaire */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={mode === "login" ? handleLogin : handleForgot}
          className="w-full max-w-md fade-up"
          data-testid={mode === "login" ? "admin-login-form" : "admin-forgot-form"}
        >
          <div className="label-mono">§ {mode === "login" ? "Accès console" : "Récupération"}</div>
          <h1 className="mt-3 font-serif text-4xl font-medium">
            {mode === "login" ? "Connexion admin" : "Mot de passe oublié"}
          </h1>
          <p className="mt-2 text-sm text-[var(--muted)]">
            {mode === "login"
              ? "Compte unique. Sessions sécurisées 12h."
              : "Un lien sera envoyé à l'email de récupération du compte."}
          </p>

          <div className="mt-8 space-y-4">
            <label className="block">
              <div className="label-mono mb-2">Email administrateur</div>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-black px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--signal)]"
                placeholder="admin@vitae-publica.tech"
                data-testid="admin-login-email"
              />
            </label>
            {mode === "login" && (
              <label className="block">
                <div className="label-mono mb-2">Mot de passe</div>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-black px-4 py-3 bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--signal)]"
                  placeholder="••••••••"
                  data-testid="admin-login-password"
                />
              </label>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-[var(--signal)] hover:bg-[var(--signal-hover)] text-[var(--paper)] py-4 label-mono flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid={mode === "login" ? "admin-login-submit" : "admin-forgot-submit"}
          >
            {loading ? "Traitement..." : mode === "login" ? "Ouvrir la console" : "Envoyer le lien"}
            <ArrowRight size={16} weight="bold" />
          </button>

          <div className="mt-6 flex items-center justify-between text-sm">
            <button
              type="button"
              onClick={() => setMode(mode === "login" ? "forgot" : "login")}
              className="underline text-[var(--ink)] hover:text-[var(--signal)]"
              data-testid="admin-toggle-mode"
            >
              {mode === "login" ? "Mot de passe oublié ?" : "← Retour connexion"}
            </button>
            <Link to="/" className="text-[var(--muted)] hover:text-[var(--ink)] underline" data-testid="admin-back-home">
              Retour au site
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
